package org.teddapp.project.ui

import androidx.compose.foundation.gestures.detectDragGesturesAfterLongPress
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.zIndex
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * État partagé pour une colonne réordonnable. Tient le drag en cours et expose un
 * `recentlyDropped` qui vaut true pendant 100 ms après un drop — utile pour le verrou
 * anti-clic post-drop (spec §8.1).
 */
class ReorderableState internal constructor() {
    internal var draggingKey by mutableStateOf<Any?>(null)
        private set
    internal var startIndex by mutableStateOf(0)
        private set
    internal var dragOffsetY by mutableStateOf(0f)
        private set
    internal val itemHeights = mutableMapOf<Any, Int>()
    internal var currentTargetIndex by mutableStateOf(0)
        private set

    /** True pendant les 100 ms qui suivent un drop. Empêche les clics fantômes (spec §8.1). */
    var recentlyDropped by mutableStateOf(false)
        internal set

    internal fun startDrag(key: Any, index: Int) {
        draggingKey = key
        startIndex = index
        currentTargetIndex = index
        dragOffsetY = 0f
    }

    internal fun updateDrag(deltaY: Float, itemCount: Int) {
        dragOffsetY += deltaY
        val unit = itemHeights[draggingKey]?.takeIf { it > 0 } ?: return
        val delta = (dragOffsetY / unit).toInt()
        currentTargetIndex = (startIndex + delta).coerceIn(0, itemCount - 1)
    }

    internal fun endDrag(): Pair<Int, Int>? {
        val from = startIndex
        val to = currentTargetIndex
        draggingKey = null
        dragOffsetY = 0f
        return if (from != to) from to to else null
    }

    internal fun cancelDrag() {
        draggingKey = null
        dragOffsetY = 0f
    }
}

@Composable
fun rememberReorderableState(): ReorderableState = remember { ReorderableState() }

/**
 * Colonne dont les items sont réordonnables par drag & drop (spec §7.5, §8.1).
 *
 * Comportement :
 *  - Verrouillé sur l'axe Y (seul l'offset Y est tracké).
 *  - Démarre sur long-press (≈300 ms par défaut Compose) — couvre desktop ET mobile
 *    sans confondre avec le scroll de la liste parente.
 *  - L'item draggé est élevé en Z et translaté visuellement ; la persistence se fait au drop.
 *  - `recentlyDropped` est à true pendant 100 ms post-drop — branche tes clics dessus pour
 *    éviter qu'un drop déclenche un onClick.
 *
 * Limites :
 *  - Suppose des hauteurs d'item raisonnablement homogènes (calcul d'index par division).
 *  - Pas de placeholder dashed — l'item se déplace, le slot d'origine apparaît vide pendant le drag.
 *  - Ne gère pas le DnD inter-conteneurs (spec §11.4 : volontairement non exposé).
 */
@Composable
fun <T> ReorderableColumn(
    items: List<T>,
    keyOf: (T) -> Any,
    state: ReorderableState,
    onReorder: (fromIndex: Int, toIndex: Int) -> Unit,
    modifier: Modifier = Modifier,
    itemContent: @Composable (index: Int, item: T, dragHandleModifier: Modifier) -> Unit,
) {
    val scope = rememberCoroutineScope()
    Column(modifier = modifier) {
        items.forEachIndexed { index, item ->
            val key = keyOf(item)
            val isDragging = state.draggingKey == key

            val handleModifier = Modifier.pointerInput(key, items.size) {
                detectDragGesturesAfterLongPress(
                    onDragStart = { state.startDrag(key, index) },
                    onDragEnd = {
                        val swap = state.endDrag()
                        if (swap != null) onReorder(swap.first, swap.second)
                        state.recentlyDropped = true
                        scope.launch {
                            delay(100)
                            state.recentlyDropped = false
                        }
                    },
                    onDragCancel = {
                        state.cancelDrag()
                        state.recentlyDropped = true
                        scope.launch {
                            delay(100)
                            state.recentlyDropped = false
                        }
                    },
                    onDrag = { change, dragAmount ->
                        change.consume()
                        // Verrouillage axe Y : on n'utilise que dragAmount.y.
                        state.updateDrag(dragAmount.y, items.size)
                    },
                )
            }

            Box(
                modifier = Modifier
                    .onGloballyPositioned { coords ->
                        state.itemHeights[key] = coords.size.height
                    }
                    .let { m ->
                        if (isDragging) {
                            m.graphicsLayer { translationY = state.dragOffsetY }.zIndex(1f)
                        } else {
                            m
                        }
                    },
            ) {
                itemContent(index, item, handleModifier)
            }
        }
    }
}
